import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBasket } from '../context/BasketContext';
import { useT } from '../i18n';
import { USER_TYPES } from '../constants/designer';
import { hasPrice } from '../constants/gallery';

// ── ADD TO BASKET ────────────────────────────────────────────────────────────
// A toggle, not an "add": pressing it again takes the product back out. Two
// controls for one binary state would need a second icon and a rule about
// which one shows, and the filled/outline pair already says which way it is.
//
// WHO SEES IT. Customers and designers — the people who buy. A SHOP does not
// get one on its own screens: a basket of your own stock is not a thing, and
// the button would sit on every tile of its own gallery. Signed-out visitors
// see it and are sent to sign in, the same as the like button does, because
// hiding it would make the feature invisible to exactly the people who have not
// joined yet.
//
// ONLY ON PRODUCTS. A designer's photo is work, not stock — there is nothing to
// put in a basket and no price to put there.
export default function BasketButton({ item, className = '' }) {
  const { user, profile } = useAuth();
  const { has, toggle, isBusy } = useBasket();
  const { t } = useT();
  const navigate = useNavigate();

  const isShop = profile?.userType === USER_TYPES.SHOP;
  // A priced listing, published, and not your own.
  const sellable = hasPrice(item) && item?.visible !== false;
  if (!sellable || isShop) return null;

  const inBasket = has(item.id);
  const busy = isBusy?.(item.id);
  const label = inBasket ? t('bsk_remove') : t('bsk_add');

  const onClick = (e) => {
    e.stopPropagation();
    if (!user?.uid) { navigate('/login'); return; }
    toggle(item);
  };

  return (
    <button
      type="button"
      className={`bskb${inBasket ? ' bskb--on' : ''} ${className}`.trim()}
      onClick={onClick}
      disabled={busy}
      aria-pressed={inBasket}
      aria-label={label}
      title={label}
    >
      <BasketIcon filled={inBasket} />
    </button>
  );
}

/* A basket, drawn as one shape rather than borrowed from a cart: this is a
   saved list, and a trolley promises a checkout that does not exist here.
 *
 * THE MARK INSIDE IS THE VERB. A basket alone names a place; a basket with a
 * plus says what pressing it does, which matters most on the picture where
 * there is no label beside it. Once the product is in, the plus becomes a
 * tick — the same shape answering "what now" with "it is done".
 *
 * The body stays OUTLINED in both states. Filling it would put the mark on a
 * solid ground of the same colour and lose it; the button's own background
 * carries the on-state instead (see .bskb--on).
 */
export function BasketIcon({ filled = false, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      {/* The body. */}
      <path d="M3.5 9h17l-1.6 9.2a2 2 0 0 1-2 1.8H7.1a2 2 0 0 1-2-1.8L3.5 9Z" />
      {/* The handle. */}
      <path d="M8.5 9 12 3.6 15.5 9" />
      {filled
        ? <path d="M9.4 14.4 11.3 16.3 14.9 12.5" strokeWidth="2.1" />
        : (
          <>
            <path d="M9.3 14.4h5.4" strokeWidth="2.1" />
            <path d="M12 11.7v5.4" strokeWidth="2.1" />
          </>
        )}
    </svg>
  );
}
